/**
 * Mensagens centralizadas para timeouts de operações CorelDRAW
 * 
 * Fornece mensagens padronizadas para diferentes tipos de timeout,
 * mantendo consistência na comunicação com o usuário.
 */

export interface TimeoutMessageOptions {
  action: string;
  totalMinutes: number;
  additionalMinutes: number;
  isFileOpen?: boolean;
}

/**
 * Gera mensagem de timeout para operações do CorelDRAW
 */
export function getTimeoutMessage(options: TimeoutMessageOptions): {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
} {
  const { action, totalMinutes, additionalMinutes, isFileOpen } = options;

  // Templates específicos por tipo de operação
  const templates = {
    // Operações de processamento/metadados
    'metadados-objetos': () => ({
      title: `Tempo de Processamento Excedido (${totalMinutes} minutos)`,
      message: 
        `A operação "${action}" ainda está sendo processada pelo CorelDRAW.\n` +
        'Este processo pode demorar em documentos grandes.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para INTERROMPER a operação`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: 'Interromper'
    }),

    // Operações de exportação
    'corel-export-batch': () => ({
      title: `Tempo de Exportação Excedido (${totalMinutes} minutos)`,
      message:
        'A exportação em lote ainda está sendo processada pelo CorelDRAW.\n' +
        'Este processo pode demorar com múltiplos objetos ou formatos complexos.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para INTERROMPER a operação`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: 'Interromper'
    }),

    'corel-export-simple': () => ({
      title: `Tempo de Exportação Excedido (${totalMinutes} minutos)`,
      message:
        'A exportação ainda está sendo processada pelo CorelDRAW.\n' +
        'Este processo pode demorar em documentos grandes.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para INTERROMPER a operação`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: 'Interromper'
    }),

    'exportar-objetos-png-lite': () => ({
      title: `Tempo de Exportação PNG Excedido (${totalMinutes} minutos)`,
      message:
        'A exportação de PNG ainda está sendo processada pelo CorelDRAW.\n' +
        'Este processo pode demorar com múltiplos objetos.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para INTERROMPER a operação`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: 'Interromper'
    }),

    'exportar-objetos-png': () => ({
      title: `Tempo de Exportação PNG Excedido (${totalMinutes} minutos)`,
      message:
        'A exportação de PNG ainda está sendo processada pelo CorelDRAW.\n' +
        'Este processo pode demorar com múltiplos objetos ou acesso via rede.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para INTERROMPER a operação`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: 'Interromper'
    }),

    // Operações de salvamento
    'salvararquivo': () => ({
      title: `Tempo de Salvamento Excedido (${totalMinutes} minutos)`,
      message:
        'O arquivo ainda está sendo salvo pelo CorelDRAW.\n' +
        'Este processo pode demorar com arquivos grandes ou acesso via rede.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para INTERROMPER a operação`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: 'Interromper'
    }),

    // Operações de abertura de arquivo
    'file-open': () => ({
      title: `Tempo de Abertura Excedido (${totalMinutes} minutos)`,
      message:
        'O arquivo ainda está sendo aberto pelo CorelDRAW.\n' +
        'Este processo pode demorar mais em arquivos grandes ou redes lentas.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para ${isFileOpen ? 'FECHAR esta janela' : 'INTERROMPER a operação'}`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: isFileOpen ? 'Fechar' : 'Interromper'
    }),

    // Template padrão para operações não mapeadas
    'default': () => ({
      title: `Tempo de Processamento Excedido (${totalMinutes} minutos)`,
      message:
        `A operação "${action}" ainda está sendo processada pelo CorelDRAW.\n` +
        'Este processo pode demorar em documentos grandes.\n\n' +
        `✅ Clique em "OK" para CONTINUAR aguardando (mais ${additionalMinutes} minutos)\n` +
        `❌ Clique em "Cancelar" para INTERROMPER a operação`,
      confirmLabel: 'Continuar Aguardando',
      cancelLabel: 'Interromper'
    })
  };

  // Buscar template específico ou usar padrão
  const templateKey = action in templates ? action : 'default';
  const template = templates[templateKey as keyof typeof templates];
  
  return template();
}

/**
 * Helper para gerar mensagens de timeout de abertura de arquivo
 */
export function getFileOpenTimeoutMessage(totalMinutes: number, additionalMinutes: number = 20): {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
} {
  return getTimeoutMessage({
    action: 'file-open',
    totalMinutes,
    additionalMinutes,
    isFileOpen: true
  });
}

